import type { DateRangeValue } from "@/components/ui/date-range-picker";
import { lastMonthsRange } from "@/lib/date-utils";
import { createTableFiltersStore } from "./createTableFiltersStore";

export interface AuditLogListFiltersState extends Record<string, unknown> {
  /** Free-text term: entity id, actor email, or a keyword from the description. */
  search: string;

  /**
   * Audit action key exactly as the backend emits it, e.g. `admin.resource.started`. Empty string
   * means "every action". The dropdown is built from the facets endpoint rather than a list
   * declared here, so `sanitize` cannot validate a restored value against a fixed set — an action
   * that no longer exists simply returns no rows, which the operator can see and clear from the
   * filter chip.
   */
  action: string;

  /** Entity type as the backend emits it (`Resource`, `Payment`, …). Empty string means "every type". */
  entityType: string;

  /** Occurred-at range, ISO strings. Both ends or neither — the API ignores a half range. */
  dateRange: DateRangeValue;
}

/**
 * True when a stored range has exactly one end. The picker only ever produces both or neither, so
 * this can only come from hand-edited storage.
 */
const isHalfRange = (range: DateRangeValue | undefined): boolean =>
  Boolean(range?.from) !== Boolean(range?.to);

/**
 * The occurred-at range opens on the last six months rather than on "no filter", matching the
 * resource, user, tenant and payment lists. It matters more here than on any of them: this table
 * grows a row per action across every workspace, so an unbounded first page is the slowest query
 * in the console and the least useful answer.
 */
const defaults: AuditLogListFiltersState = {
  search: "",
  action: "",
  entityType: "",
  dateRange: lastMonthsRange(),
};

/**
 * Filter and paging state for the admin audit log, cached in sessionStorage.
 *
 * Bump `version` in step with any change to the shape above — a stored filter the backend no
 * longer accepts comes back as a 400 on something the operator cannot see.
 */
export const useAuditLogFiltersStore =
  createTableFiltersStore<AuditLogListFiltersState>({
    name: "admin.auditLogs.filters",
    version: 1,
    defaults,
    // 20, not the store's default 50: audit rows carry a description and wrap to two lines, so a
    // screenful here is a third of what it is on the resource list.
    defaultPageSize: 20,
    sanitize: (filters) => ({
      ...filters,

      // Exactly one end is not a range: the query applies fromDate/toDate together, so a stored
      // half would show as a filter chip that narrows nothing. An *empty* range is left alone — it
      // is the operator having deliberately cleared the six-month default to search all time, and
      // replacing it here would silently put the default back on every reload.
      dateRange: isHalfRange(filters.dateRange)
        ? defaults.dateRange
        : filters.dateRange,
    }),
  });
